import React, { Suspense, lazy, useState, useEffect, useRef } from 'react';
import { Routes, Route, useSearchParams, useNavigate, Navigate } from 'react-router-dom';
import AuthModal from './components/AuthModal';
import AuthCallback from './components/AuthCallback';
import { AuthProvider, useAuthContext } from './contexts/AuthContext';
import { auth } from './services/api';
import { PKCE } from './utils/pkce';
import AppShell from './components/shell/AppShell';
import { Spinner } from './components/ds';
import { useIsAdmin } from './hooks/useIsAdmin';

// Страницы грузятся по роутам отдельными чанками (AIT-398): гость получает только
// лендинг, админские панели и конфигуратор не попадают в стартовый бандл.
const ProfilePage = lazy(() => import('./components/ProfilePage'));
const MultiAgentPage = lazy(() => import('./components/MultiAgentPage'));
const AdminPage = lazy(() => import('./components/AdminPage'));
const ConnectorPage = lazy(() => import('./components/ConnectorPage'));
const DatasetsPage = lazy(() => import('./components/DatasetsPage'));
const FineTuningGraphPage = lazy(() => import('./components/FineTuningGraphPage'));

type ModalKind = 'login' | 'register' | null;

const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuthContext();
  if (isLoading) return null;
  if (!isAuthenticated) return <Navigate to="/" replace />;
  return <>{children}</>;
};

const RequireAdmin: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuthContext();
  const isAdmin = useIsAdmin();
  if (isLoading) return <Spinner label="Loading..." />;
  if (!isAuthenticated || !isAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
};

/** Гость: Multi-Agent. Авторизованный: редирект на Connector. */
const DefaultLanding: React.FC<{ onShowLoginModal: () => void }> = ({ onShowLoginModal }) => {
  const { isAuthenticated, isLoading } = useAuthContext();
  if (isLoading) return <Spinner label="Loading..." />;
  if (isAuthenticated) return <Navigate to="/connector" replace />;
  return <MultiAgentPage onShowLoginModal={onShowLoginModal} />;
};

const AuthWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuthContext();
  const [isProcessingAuth, setIsProcessingAuth] = useState(false);
  const processedTokens = useRef<Set<string>>(new Set());

  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      navigate(`/auth/auth-link/callback?token=${token}`, { replace: true });
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const error = searchParams.get('error');

    if (error && !isProcessingAuth) {
      navigate('/', { replace: true });
      return;
    }

    if (code && state && !isProcessingAuth && !processedTokens.current.has(code) && !isAuthenticated) {
      navigate('/', { replace: true });
      setIsProcessingAuth(true);

      const handleGoogleCallback = async () => {
        try {
          const pkceData = PKCE.getPKCEData();
          if (!pkceData.codeVerifier || pkceData.state !== state) {
            throw new Error('Invalid PKCE data or state mismatch');
          }
          const response = await auth.googleCallback(code, pkceData.codeVerifier);
          if (response.success && response.data) {
            login(response.data.email, response.data.name, response.data.company_name);
          }
          processedTokens.current.add(code);
          PKCE.clearPKCEData();
        } catch (err: any) {
          console.error('Google OAuth callback error:', err);
          processedTokens.current.add(code);
          PKCE.clearPKCEData();
        } finally {
          setIsProcessingAuth(false);
        }
      };

      handleGoogleCallback();
    }
  }, [searchParams, login, navigate, isProcessingAuth, isAuthenticated]);

  return (
    <>
      {children}
      {isProcessingAuth && (
        <div className="auth-processing-overlay">
          <div className="auth-processing-content">
            <span className="ds-spinner ds-spinner--lg loading-spinner" aria-hidden="true" />
            <h3>Authorization...</h3>
            <p>Processing your request</p>
          </div>
        </div>
      )}
    </>
  );
};

const App: React.FC = () => {
  const [activeModal, setActiveModal] = useState<ModalKind>(null);
  const openLogin = () => setActiveModal('login');

  /** Все страницы живут в одном каркасе; модалка входа общая. */
  const inShell = (page: React.ReactNode) => (
    <AppShell onLoginClick={openLogin}>
      {page}
      {activeModal && <AuthModal type={activeModal} isOpen={true} onClose={() => setActiveModal(null)} />}
    </AppShell>
  );

  return (
    <AuthProvider>
      <AuthWrapper>
        <Suspense fallback={<Spinner label="Loading..." />}>
          <Routes>
            <Route path="/" element={inShell(<DefaultLanding onShowLoginModal={openLogin} />)} />
            <Route path="/multi-agent" element={inShell(<MultiAgentPage onShowLoginModal={openLogin} />)} />
            <Route path="/auth/auth-link" element={<AuthCallback />} />
            <Route path="/auth/auth-link/callback" element={<AuthCallback />} />
            <Route path="/profile" element={<RequireAuth>{inShell(<ProfilePage />)}</RequireAuth>} />
            <Route path="/connector" element={<RequireAuth>{inShell(<ConnectorPage />)}</RequireAuth>} />
            <Route path="/admin" element={<RequireAdmin>{inShell(<AdminPage />)}</RequireAdmin>} />
            <Route
              path="/admin/fine-tuning-graph"
              element={<RequireAdmin>{inShell(<FineTuningGraphPage />)}</RequireAdmin>}
            />
            <Route path="/datasets" element={<RequireAuth>{inShell(<DatasetsPage />)}</RequireAuth>} />
          </Routes>
        </Suspense>
      </AuthWrapper>
    </AuthProvider>
  );
};

export default App;
